import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle, Clock, Info, Scales, TreeStructure, Warning } from '@phosphor-icons/react'
import { ApiError, apiRequest, errorMessage, jsonBody } from '../api/client'
import type {
  ActivePlan,
  OwnerPlanV4MaterialInput,
  OwnerPlanV4Preview,
  OwnerPlanV4PreviewItem,
  OwnerPlanV4PreviewModifier,
} from '../api/types'
import { queryClient } from '../app/queryClient'
import { OwnerPlanV4LifecyclePanel } from './OwnerPlanV4LifecyclePanel'

const shoulderOptions = [
  { value: 'owner-band-yellow', label: 'Žlutá odporová guma' },
  { value: 'owner-shoulder-extension-dowel-v1', label: 'Tyč / násada' },
]

const blockerLabels: Record<string, string> = {
  'starting.owner_input_required': 'Doplň přesnou pracovní váhu. Coach ji nesmí odhadnout.',
  'starting.history_confirmation_required': 'Potvrď nabídnutou srovnatelnou hodnotu z historie.',
  'preparation.owner_choice_required': 'Vyber pomůcku pro lehkou extenzi ramen.',
  'preparation.support_missing': 'Zadej jednu přesnou oporu vyšší než pracovní opora 45 cm.',
}

const inputStatusLabels: Record<OwnerPlanV4MaterialInput['status'], string> = {
  Blocked: 'Blokováno',
  HistoryCandidateAwaitingConfirmation: 'Čeká na potvrzení historie',
  OwnerInputRequired: 'Čeká na tvoji hodnotu',
  ConfirmedOwnerInput: 'Potvrzeno tebou',
  ConfirmedComparableHistory: 'Potvrzeno z historie',
  StaleHistoryConfirmation: 'Historii je nutné znovu ověřit',
}

function exerciseName(slug: string): string {
  return slug
    .split('-')
    .map((part) => part.length === 0 ? part : `${part[0].toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

function numberValue(value: string): number | null {
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

const dayLabels: Record<string, string> = {
  Monday: 'Pondělí',
  Tuesday: 'Úterý',
  Wednesday: 'Středa',
  Thursday: 'Čtvrtek',
  Friday: 'Pátek',
  Saturday: 'Sobota',
  Sunday: 'Neděle',
}

const doseLabels: Record<string, string> = {
  Repetitions: 'opakování',
  HoldSeconds: 'sekund',
  PracticeMinutes: 'minut',
  Completion: 'dokončení',
}

function targetLabel(item: Pick<OwnerPlanV4PreviewItem, 'doseUnit' | 'exactTargets'>): string {
  const unit = doseLabels[item.doseUnit] ?? item.doseUnit
  if (item.exactTargets.length === 0) return `Bez pracovních slotů · ${unit}`
  if (item.exactTargets.every((target) => target === item.exactTargets[0])) {
    return `${item.exactTargets.length} sérií × ${item.exactTargets[0]} ${unit}`
  }
  return `${item.exactTargets.join(' + ')} ${unit}`
}

function modifierLabel(modifier: OwnerPlanV4PreviewModifier): string {
  if (modifier.identityDisplayName) return modifier.identityDisplayName
  if (modifier.numericValue !== null) {
    const suffix = modifier.kind === 'ExternalLoadKilograms'
      ? ' kg'
      : modifier.kind.includes('Height')
        ? ' cm'
        : modifier.kind.includes('Seconds')
          ? ' s'
          : ''
    return `${modifier.numericValue}${suffix}`
  }
  return modifier.textValue ?? modifier.kind
}

function shortHash(value: string | null): string {
  return value ? `${value.slice(0, 10)}…${value.slice(-6)}` : 'nevytvořen'
}

function activationBlockerLabel(code: string, inProgressCount: number): string {
  if (code === 'transition.source_workout_in_progress') {
    return `${inProgressCount} rozpracované tréninky v3 je nutné dokončit nebo výslovně opustit.`
  }
  if (code === 'transition.compilation_blocked') return 'Compiler stále eviduje blokující vstup.'
  if (code === 'transition.target_contract_count_mismatch') return 'Počet položek nebo cílů neodpovídá schválenému kontraktu.'
  if (code.startsWith('transition.material.')) return 'Chybí potvrzený materiální vstup s dohledatelnou provenance.'
  return code
}
export function OwnerPlanV4PreparationPanel({ activePlan }: { activePlan: ActivePlan }) {
  const preview = useQuery({
    queryKey: ['owner-v4-preview'],
    queryFn: () => apiRequest<OwnerPlanV4Preview>('/plans/owner-v4-preview'),
    retry: false,
  })
  const [loads, setLoads] = useState<Record<string, string>>({})
  const [shoulderChoice, setShoulderChoice] = useState('')
  const [supportHeight, setSupportHeight] = useState('')

  useEffect(() => {
    if (!preview.data) return
    setLoads(Object.fromEntries(preview.data.materialInputs.map((item) => [
      item.stablePlanItemKey,
      item.resolvedExternalLoadKilograms === null
        ? ''
        : String(item.resolvedExternalLoadKilograms),
    ])))
    setShoulderChoice(preview.data.preparation.shoulderExtensionEquipmentStableKey ?? '')
    setSupportHeight(preview.data.preparation.nextEasierSupportHeightCentimeters === null
      ? ''
      : String(preview.data.preparation.nextEasierSupportHeightCentimeters))
  }, [preview.data])

  const updatePreview = (result: OwnerPlanV4Preview) => {
    queryClient.setQueryData(['owner-v4-preview'], result)
  }
  const saveLoad = useMutation({
    mutationFn: ({ item, kilograms }: { item: OwnerPlanV4MaterialInput; kilograms: number }) =>
      apiRequest<OwnerPlanV4Preview>(
        `/plans/owner-v4-preview/material-inputs/${encodeURIComponent(item.stablePlanItemKey)}`,
        {
          method: 'PUT',
          body: jsonBody({
            externalLoadKilograms: kilograms,
            expectedInputStateVersion: item.stateVersion,
          }),
        },
      ),
    onSuccess: updatePreview,
  })
  const confirmHistory = useMutation({
    mutationFn: (item: OwnerPlanV4MaterialInput) => {
      if (!item.historyCandidate) throw new Error('Historická hodnota už není dostupná.')
      return apiRequest<OwnerPlanV4Preview>(
        `/plans/owner-v4-preview/material-inputs/${encodeURIComponent(item.stablePlanItemKey)}/confirm-history`,
        {
          method: 'POST',
          body: jsonBody({
            externalLoadKilograms: item.historyCandidate.externalLoadKilograms,
            candidateHash: item.historyCandidate.candidateHash,
            exposureEvaluationId: item.historyCandidate.exposureEvaluationId,
            expectedInputStateVersion: item.stateVersion,
          }),
        },
      )
    },
    onSuccess: updatePreview,
  })
  const savePreparation = useMutation({
    mutationFn: ({ equipment, height }: { equipment: string; height: number }) =>
      apiRequest<OwnerPlanV4Preview>('/plans/owner-v4-preview/preparation', {
        method: 'PUT',
        body: jsonBody({
          shoulderExtensionEquipmentStableKey: equipment,
          nextEasierSupportHeightCentimeters: height,
          expectedShoulderChoiceStateVersion:
            preview.data?.preparation.shoulderChoiceStateVersion ?? null,
          expectedSupportHeightStateVersion:
            preview.data?.preparation.supportHeightStateVersion ?? null,
        }),
      }),
    onSuccess: updatePreview,
  })

  if (preview.isPending) {
    return (
      <section className="content-section owner-v4-preview" aria-busy="true">
        <p className="eyebrow">Owner plán v4</p>
        <p className="owner-v4-preview__loading">Ověřuji přesné vstupy preview…</p>
      </section>
    )
  }
  if (preview.isError) {
    if (preview.error instanceof ApiError && preview.error.status === 404) return null
    return (
      <section className="content-section owner-v4-preview">
        <div className="owner-v4-preview__error" role="alert">
          <Warning size={20} />
          <div><strong>Preview v4 se nepodařilo načíst</strong><p>{errorMessage(preview.error)}</p></div>
          <button className="button button--quiet" type="button" onClick={() => preview.refetch()}>Zkusit znovu</button>
        </div>
      </section>
    )
  }

  const data = preview.data
  const isCompiled = data.status === 'Ready' || data.status === 'Activated'
  const activationReady = isCompiled && data.transition.isReady
  const remainingGateCount = data.blockers.length + data.transition.activationBlockers.length
  const support = numberValue(supportHeight)
  const preparationValid = shoulderOptions.some((option) => option.value === shoulderChoice)
    && support !== null && support > 45 && support <= 300
  const mutationError = saveLoad.error ?? confirmHistory.error ?? savePreparation.error

  return (
    <section className="content-section owner-v4-preview" aria-labelledby="owner-v4-preview-title">
      <div className="section-heading owner-v4-preview__heading">
        <div>
          <p className="eyebrow">{data.status === 'Activated' ? 'Aktivní plán / ověřený rollback' : 'Bezpečná příprava / bez automatické aktivace'}</p>
          <h2 id="owner-v4-preview-title"><span aria-hidden="true">00 /</span> Owner plán v4</h2>
          <p>{data.status === 'Activated'
            ? 'V4 je aktivní. Zachovaný v3 plán a jeho historie zůstávají nedotčeným rollback bodem.'
            : 'Compiler připraví samostatnou instanci. Současný v3 plán i jeho historie zůstávají nedotčené.'}</p>
        </div>
        <span className={`status-pill ${data.status === 'Activated' ? 'status-pill--success' : 'status-pill--warning'}`}>
          {data.status === 'Activated'
            ? 'Aktivní'
            : activationReady
              ? 'Přechod připraven'
              : isCompiled
                ? `${remainingGateCount} zbývajících bran`
                : `${data.blockers.length} blokujících vstupů`}
        </span>
      </div>

      <div className="owner-v4-preview__facts">
        <div><span>Položky</span><strong>{data.planItemCount}</strong></div>
        <div><span>Cíle</span><strong>{data.goalCount}</strong></div>
        <div><span>Verze</span><strong>v{data.planVersion}</strong></div>
        <div><span>Instance</span><strong>{data.planInstanceId ? data.planInstanceId.slice(0, 8) : 'nevytvořena'}</strong></div>
      </div>

      {isCompiled ? (
        <>
          <div className="owner-v4-preview__ready" role="status">
            {data.status === 'Activated' || activationReady
              ? <CheckCircle size={26} weight="fill" />
              : <Warning size={26} />}
            <div>
              <strong>{data.status === 'Activated'
                ? 'V4 instance je aktivní a server ji znovu ověřil'
                : 'Neaktivní v4 instance je zkompilovaná a ověřená'}</strong>
              <p>{data.status === 'Activated'
                ? `Aktivní plán obsahuje přesně ${data.planItemCount} položek a ${data.goalCount} cílů. V3 zůstává zachovaný pouze pro audit a bezpečný návrat.`
                : `Obsahuje přesně ${data.planItemCount} položek a ${data.goalCount} cílů. Aktivní v3 ani jeho historie se během preview nezměnily.`}</p>
            </div>
          </div>

          {!activationReady && (
            <div className="owner-v4-preview__blockers" aria-label="Zbývající aktivační brány">
              <Info size={20} />
              <div>
                <strong>Aktivace je stále bezpečně blokovaná</strong>
                <ul>
                  {data.transition.activationBlockers.map((blocker) => (
                    <li key={blocker}>{activationBlockerLabel(blocker, data.transition.sourceInProgressWorkoutCount)}</li>
                  ))}
                  <li>Produkční záloha, worker, offline, browser, security a real OpenAI důkazy se potvrzují samostatně.</li>
                </ul>
              </div>
            </div>
          )}

          <div className="owner-v4-preview__hashes" aria-label="Identity připraveného plánu">
            <div><span>Source hash</span><strong title={data.sourceContentHash}>{shortHash(data.sourceContentHash)}</strong></div>
            <div><span>Instance hash</span><strong title={data.artifactContentHash ?? undefined}>{shortHash(data.artifactContentHash)}</strong></div>
            <div><span>Zdroj v3</span><strong>{data.transition.sourcePlanName} v{data.transition.sourcePlanVersion}</strong></div>
            <div><span>Historie</span><strong>{data.transition.sourceCompletedWorkoutCount} dokončených tréninků</strong></div>
          </div>

          <div className="owner-v4-preview__days" aria-label="Odhad délky dnů">
            {data.days.map((day) => (
              <div key={day.dayOfWeek}>
                <Clock size={18} />
                <span>{dayLabels[day.dayOfWeek] ?? day.dayOfWeek}</span>
                <strong>{day.estimatedMinutes} min</strong>
                <small>{day.estimatedLowerMinutes}–{day.estimatedUpperMinutes} min · {day.targetItemCount} položek · {day.materializedSlotCount} slotů</small>
              </div>
            ))}
          </div>

          <div className="owner-v4-preview__migration-note">
            <TreeStructure size={21} />
            <p>Všechny pracovní položky v4 začnou v kalibraci. Streaky ani top-range potvrzení z v3 se nekopírují. Po aktivaci se v3 skryje z běžného seznamu, nesmaže se a zůstane rollback bodem.</p>
          </div>

          <OwnerPlanV4LifecyclePanel preview={data} activePlan={activePlan} />

          <details className="owner-v4-preview__prescription">
            <summary>Zkontrolovat všech {data.planItemCount} přesných předpisů</summary>
            <div className="owner-v4-preview__prescription-body">
              {data.days.map((day) => (
                <section key={day.dayOfWeek}>
                  <h3>{dayLabels[day.dayOfWeek] ?? day.dayOfWeek}</h3>
                  <div className="owner-v4-preview__item-table">
                    {data.items.filter((item) => item.dayOfWeek === day.dayOfWeek).map((item) => (
                      <article key={item.stableKey}>
                        <span className="owner-v4-preview__item-order">{item.sortOrder}</span>
                        <div>
                          <strong>{item.exerciseName}</strong>
                          <small>{item.stableKey} · {item.intent} · {item.sessionRole}</small>
                          <p>{targetLabel(item)}</p>
                          {item.modifiers.length > 0 && <p>{item.modifiers.map(modifierLabel).join(' · ')}</p>}
                          {item.progressionStateKey && <p>Uzel: {item.progressionStateKey}</p>}
                          {item.sourceRange && <p>Rozsah policy: {item.sourceRange.minimum}–{item.sourceRange.maximum}, krok {item.sourceRange.step}</p>}
                          {item.preparationSteps.length > 0 && (
                            <ol>
                              {item.preparationSteps.map((step) => (
                                <li key={step.stableKey}>{step.exerciseName}: {targetLabel(step)}</li>
                              ))}
                            </ol>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </details>
        </>
      ) : (
        <>
          <div className="owner-v4-preview__blockers" aria-label="Co ještě chybí">
            <Info size={20} />
            <div>
              <strong>Co ještě chybí</strong>
              <ul>
                {data.blockers.map((blocker) => (
                  <li key={`${blocker.stableKey}-${blocker.code}`}>
                    {blockerLabels[blocker.code] ?? `Je nutné doplnit přesný vstup pro ${blocker.stableKey}.`}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form
            className="owner-v4-preparation-form"
            onSubmit={(event) => {
              event.preventDefault()
              if (preparationValid && support !== null) {
                savePreparation.mutate({ equipment: shoulderChoice, height: support })
              }
            }}
          >
            <div className="owner-v4-preparation-form__copy">
              <Scales size={22} />
              <div><strong>Přípravné vybavení</strong><p>Vyber skutečnou pomůcku a jednu oporu vyšší než 45 cm. Coach hodnoty nevymýšlí.</p></div>
            </div>
            <label>
              <span>Extenze ramen</span>
              <select value={shoulderChoice} onChange={(event) => setShoulderChoice(event.currentTarget.value)}>
                <option value="">Vyber pomůcku</option>
                {shoulderOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label>
              <span>Vyšší opora</span>
              <div className="input-with-unit">
                <input
                  aria-label="Výška vyšší opory"
                  type="number"
                  inputMode="decimal"
                  min="45.1"
                  max="300"
                  step="0.1"
                  value={supportHeight}
                  onChange={(event) => setSupportHeight(event.currentTarget.value)}
                />
                <b>cm</b>
              </div>
            </label>
            <button className="button button--quiet" type="submit" disabled={!preparationValid || savePreparation.isPending}>
              {savePreparation.isPending ? 'Ukládám…' : 'Uložit přípravu'}
            </button>
          </form>

          <div className="owner-v4-load-list">
            <div className="owner-v4-load-list__head">
              <span>Materiální vstup</span><span>Pracovní váha</span><span>Stav</span><span aria-hidden="true" />
            </div>
            {data.materialInputs.map((item) => {
              const name = exerciseName(item.exerciseSlug)
              const kilograms = numberValue(loads[item.stablePlanItemKey] ?? '')
              const valid = kilograms !== null && kilograms >= 0 && kilograms <= 1000
              const savingThis = saveLoad.isPending
                && saveLoad.variables.item.stablePlanItemKey === item.stablePlanItemKey
              return (
                <article className="owner-v4-load-row" key={item.stablePlanItemKey}>
                  <div><strong>{name}</strong><small>{item.stablePlanItemKey}</small></div>
                  <label>
                    <span className="sr-only">Pracovní váha pro {name}</span>
                    <div className="input-with-unit">
                      <input
                        aria-label={`Pracovní váha pro ${name}`}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="1000"
                        step="0.25"
                        value={loads[item.stablePlanItemKey] ?? ''}
                        onChange={(event) => {
                          const value = event.currentTarget.value
                          setLoads((current) => ({
                            ...current,
                            [item.stablePlanItemKey]: value,
                          }))
                        }}
                      />
                      <b>kg</b>
                    </div>
                  </label>
                  <span className={`owner-v4-load-row__status owner-v4-load-row__status--${item.status.toLowerCase()}`}>
                    {inputStatusLabels[item.status]}
                  </span>
                  <div className="owner-v4-load-row__actions">
                    {item.historyCandidate && (
                      <button className="text-link" type="button" onClick={() => confirmHistory.mutate(item)} disabled={confirmHistory.isPending}>
                        Použít {item.historyCandidate.externalLoadKilograms} kg z historie
                      </button>
                    )}
                    <button
                      className="button button--quiet"
                      type="button"
                      disabled={!valid || saveLoad.isPending}
                      onClick={() => valid && kilograms !== null && saveLoad.mutate({ item, kilograms })}
                    >
                      {savingThis ? 'Ukládám…' : item.stateVersion === null ? 'Potvrdit' : 'Upravit'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        </>
      )}

      {mutationError && <p className="inline-error" role="alert">{errorMessage(mutationError)}</p>}
    </section>
  )
}